import { useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import { type FunctionComponent, useContext, useEffect } from 'react';
import { type ProductPageQuery, type ProductPageQueryVariables } from '../../../__generated__/graphql-client-types';
import { StyledLink } from '../../../components/common-components/link/styled-link.component';
import { PageLoading } from '../../../components/common-components/loading/loading.component';
import { PageContainer } from '../../../components/common-components/page/page-container/page-container.component';
import { type ProductPageType } from '../../../components/common-components/page/page-container/page-container.types';
import { FiveHundred } from '../../../components/error-components/500/500.component';
import { MediaGallery } from '../../../components/media-gallery/media-gallery.component';
import { BrandLogo } from '../../../components/product-components/brand-logo/brand-logo.component';
import { DiscontinuedBuyBox } from '../../../components/product-components/discontinued-buy-box/discontinued-buy-box.component';
import { ProductSummary } from '../../../components/product-components/product-summary/product-summary.component';
import { SUPPORT_FAQ_LINK } from '../../../constants/links';
import { DEFAULT_PDP_PRODUCT_STATUS, INFO_DISCONTINUED_PRODUCT_FAMILY } from '../../../constants/product';
import { VariantContext } from '../../../contexts/variant/variant.context';
import {
	convertRegionalStatusToAvailabilityEnum,
	createProductName,
	createProductPageTitle
} from '../../../helpers/product-helper/product.helper';
import { useEmployee } from '../../../hooks/apollo/employee/employee.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useNotification } from '../../../hooks/notification/notification.hooks';
import { PRODUCT_PAGE } from '../../../queries/product/product.queries';
import { ProductDetailsSection } from '../product-details/components/product-details-section/product-details-section.component';

// dynamically import internal info to reduce bundle size for non-internal users
const LoadableInternalProductInfo = loadable(
	() =>
		import(
			/* webpackChunkName: "internalproductinformation" */ '../../../components/product-components/internal-product-information/internal-product-information.component'
		),
	{
		resolveComponent: ({ InternalProductInformation }) => InternalProductInformation
	}
);

export type DiscontinuedProductProps = {
	productUid: number;
	productFamilyId: number;
	pageName: string;
};

export const DiscontinuedProduct: FunctionComponent<DiscontinuedProductProps> = ({ productUid, productFamilyId, pageName }) => {
	const { currentVariant } = useContext(VariantContext);
	const { notifyWarning } = useNotification();
	const { employee } = useEmployee();
	const { data, error } = useQuery<ProductPageQuery, ProductPageQueryVariables>(PRODUCT_PAGE, {
		variables: { id: productFamilyId, productStatus: DEFAULT_PDP_PRODUCT_STATUS }
	});

	useDyRecommendationContext({ type: DyRecommendationContextType.PRODUCT_PAGE, data: [`${currentVariant.id}`] });

	useEffect(() => {
		notifyWarning(INFO_DISCONTINUED_PRODUCT_FAMILY, 7000);

		// This useEffect is expected to only run onMount
	}, []);

	if (error) {
		return <FiveHundred />;
	}
	if (data?.productFamily?.__typename !== 'ProductFamily') {
		return <PageLoading />;
	}

	const product = data.productFamily;
	const brandName = product.brandInfo.name;
	const metaDescription = `Save on the ${brandName} ${currentVariant.modelNumber} from Build.com. Low Prices + Fast & Free Shipping on Most Orders. Find reviews, expert advice, manuals, specs & more.`;
	const pageTitle = createProductPageTitle(brandName, currentVariant.modelNumber, product.title);
	const breadcrumbs = [...product.breadcrumbs, { name: `${brandName} ${product.productId}` }];

	return (
		<PageContainer<ProductPageType>
			brandName={brandName}
			canonicalURL={product.url}
			imagePublicId={currentVariant.image.id}
			isInStock={false}
			itemAvailability={convertRegionalStatusToAvailabilityEnum(String(currentVariant.regionalStatus))}
			metaDescription={metaDescription}
			modelNumber={currentVariant.modelNumber}
			pageTitle={pageTitle}
			price={currentVariant.priceInfo.current}
			productDescription={product.title}
			productId={`${product.id}`}
			productName={createProductName(brandName, currentVariant.modelNumber)}
			productUrl={currentVariant.url}
			ratingCount={product.reviewSummary.reviewCount}
			ratingValue={product.reviewSummary.ratingValue}
			breadcrumbs={breadcrumbs}
			showUpdateLocation={true}>
			<>
				{/* Product Header Section */}
				<section className="mt5-ns">
					<p className="theme-error tc f3">
						The {product.brandInfo.name} {currentVariant.modelNumber} has been discontinued
					</p>
					<p className="theme-error tc f3">** See similar or replacement items below **</p>
				</section>
				{/* Recs */}
				<section>
					<div id="dy-recs-product-page-1" />
				</section>
				{/* Product Section */}
				<section data-testid="product-details-product-section">
					<div className={'cf flex db-ns flex-column'}>
						<div className="fl w-50-ns order-1" data-testid="product-details-product-section-container">
							{/* Product Image */}
							<div className={'ph3-ns v-top'}>
								<BrandLogo brandName={product.brandInfo.name} imageId={product.brandInfo.logo.id} url={product.url} />
								<div>
									<MediaGallery
										items={product.media}
										variantImageId={currentVariant.image.id}
										currentVariantImage={currentVariant.image}
									/>
								</div>
							</div>
						</div>
						<div className={'fl w-50-ns order-0'}>
							<div className="db dn-ns ph3 monospace f7 mt2">Item # bci{productUid}</div>
							{/* Product Details */}
							<ProductSummary
								brand={brandName}
								modelNumber={currentVariant.modelNumber}
								title={product.title}
								collectionName={product.collection ? product.collection.name : ''}
								shouldClampTitle={false}
								reviewSummary={product.reviewSummary}
								productUid={currentVariant.id}
							/>
							<div className="db dn-ns pl3 pt1">
								<DiscontinuedBuyBox
									className="mt3"
									manufacturer={product.brandInfo.name}
									collection={product.collection}
									replacements={product.replacementModels}
								/>
							</div>
						</div>
						<div className={'fr w-100 w-50-ns order-2'}>
							<div className={'mt5 mt3-ns'}>
								<div className={'flex flex-column flex-wrap w-100 db-ns'}>
									<div className="db-ns dn">
										<DiscontinuedBuyBox
											className="mt3"
											manufacturer={product.brandInfo.name}
											collection={product.collection}
											replacements={product.replacementModels}
										/>
									</div>
								</div>
							</div>
							<div className="flex flex-column flex-row-ns justify-between pt2 pb1">
								{product.replacementPartsUrl && (
									<StyledLink url={product.replacementPartsUrl} className={'dib-ns dn mb2'}>
										{brandName} {product.productId} Replacement Parts
									</StyledLink>
								)}
								<StyledLink url={SUPPORT_FAQ_LINK} className={`dib-ns dn mb2`}>
									Return & Shipping Details
								</StyledLink>
							</div>
						</div>
					</div>
				</section>

				{/* Detail Section */}
				{pageName === 'pdp' && (
					<section>
						<ProductDetailsSection product={product} familyId={productFamilyId} />
					</section>
				)}
				{employee.isAuthenticated && (
					<div className="ma2 dn db-ns">
						<LoadableInternalProductInfo familyId={productFamilyId} />
					</div>
				)}
			</>
		</PageContainer>
	);
};
